/**
 * One-shot SVG overlay effects for active abilities (spec §3.6).
 *
 * Each effect creates an absolutely-positioned overlay over the board, plays
 * a short animation, then self-removes. They never block the lock state —
 * abilities run their own resolve() afterward; FX is purely cosmetic juice.
 */

const SVG_NS = "http://www.w3.org/2000/svg";

function spawnOverlay(host: HTMLElement): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("class", "ability-fx");
  svg.style.position = "absolute";
  svg.style.inset = "0";
  svg.style.pointerEvents = "none";
  svg.style.overflow = "visible";
  const rect = host.getBoundingClientRect();
  svg.setAttribute("viewBox", `0 0 ${rect.width} ${rect.height}`);
  host.appendChild(svg);
  return svg;
}

/** Convert a client-space (x,y) into the overlay-host's local coords. */
function toLocal(host: HTMLElement, x: number, y: number): { x: number; y: number } {
  const r = host.getBoundingClientRect();
  return { x: x - r.left, y: y - r.top };
}

function clear(svg: SVGSVGElement, ms: number): void {
  setTimeout(() => svg.remove(), ms);
}

/** Hammer: shockwave ring + radial debris shards from the destroyed tile. */
export function fxHammer(host: HTMLElement, cx: number, cy: number): void {
  const svg = spawnOverlay(host);
  const p = toLocal(host, cx, cy);

  // Expanding shockwave ring.
  const ring = document.createElementNS(SVG_NS, "circle");
  ring.setAttribute("cx", String(p.x));
  ring.setAttribute("cy", String(p.y));
  ring.setAttribute("r", "10");
  ring.setAttribute("fill", "none");
  ring.setAttribute("stroke", "#ffe69a");
  ring.setAttribute("stroke-width", "3");
  svg.appendChild(ring);
  ring.animate(
    [
      { r: 10, opacity: 1, strokeWidth: 4 } as unknown as Keyframe,
      { r: 60, opacity: 0, strokeWidth: 1 } as unknown as Keyframe,
    ],
    { duration: 500, easing: "cubic-bezier(0.2, 0.7, 0.3, 1)", fill: "forwards" },
  );

  // 10 debris shards thrown outward.
  for (let i = 0; i < 10; i++) {
    const angle = (i / 10) * Math.PI * 2 + Math.random() * 0.4;
    const dist = 50 + Math.random() * 28;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist;
    const shard = document.createElementNS(SVG_NS, "rect");
    shard.setAttribute("x", String(p.x - 3));
    shard.setAttribute("y", String(p.y - 2));
    shard.setAttribute("width", "6");
    shard.setAttribute("height", "4");
    shard.setAttribute("rx", "1.5");
    shard.setAttribute("fill", i % 2 ? "#ffd54a" : "#bfe6ff");
    svg.appendChild(shard);
    shard.animate(
      [
        { transform: "translate(0,0) rotate(0deg)", opacity: 1 },
        { transform: `translate(${dx}px, ${dy}px) rotate(${360 + Math.random() * 360}deg)`, opacity: 0 },
      ],
      { duration: 520 + Math.random() * 120, easing: "cubic-bezier(0.2, 0.5, 0.3, 1)", fill: "forwards" },
    );
  }

  clear(svg, 700);
}

/** Swap: an arc from A to B with a glow trail; both cells flash. */
export function fxSwap(
  host: HTMLElement,
  ax: number, ay: number,
  bx: number, by: number,
): void {
  const svg = spawnOverlay(host);
  const a = toLocal(host, ax, ay);
  const b = toLocal(host, bx, by);

  // Two flashing pulses on each end-point.
  for (const pt of [a, b]) {
    const pulse = document.createElementNS(SVG_NS, "circle");
    pulse.setAttribute("cx", String(pt.x));
    pulse.setAttribute("cy", String(pt.y));
    pulse.setAttribute("r", "20");
    pulse.setAttribute("fill", "none");
    pulse.setAttribute("stroke", "#3fd6e8");
    pulse.setAttribute("stroke-width", "3");
    svg.appendChild(pulse);
    pulse.animate(
      [{ r: 14, opacity: 1 }, { r: 38, opacity: 0 }] as Keyframe[],
      { duration: 480, easing: "ease-out", fill: "forwards" },
    );
  }

  // Two arrowheads circling between the two cells along a curved arc.
  const midX = (a.x + b.x) / 2;
  const midY = (a.y + b.y) / 2;
  const nx = -(b.y - a.y);
  const ny = b.x - a.x;
  const norm = Math.hypot(nx, ny) || 1;
  const bulge = 22; // height of the arc above the midpoint
  for (const dir of [1, -1]) {
    const ctrlX = midX + (nx / norm) * bulge * dir;
    const ctrlY = midY + (ny / norm) * bulge * dir;
    const path = document.createElementNS(SVG_NS, "path");
    const startX = dir === 1 ? a.x : b.x;
    const startY = dir === 1 ? a.y : b.y;
    const endX = dir === 1 ? b.x : a.x;
    const endY = dir === 1 ? b.y : a.y;
    path.setAttribute("d", `M ${startX} ${startY} Q ${ctrlX} ${ctrlY} ${endX} ${endY}`);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", dir === 1 ? "#ff5fd0" : "#3fd6e8");
    path.setAttribute("stroke-width", "3");
    path.setAttribute("stroke-linecap", "round");
    svg.appendChild(path);
    const len = path.getTotalLength();
    path.setAttribute("stroke-dasharray", String(len));
    path.setAttribute("stroke-dashoffset", String(len));
    path.animate(
      [
        { strokeDashoffset: len, opacity: 0.9 },
        { strokeDashoffset: 0, opacity: 0.9, offset: 0.7 },
        { strokeDashoffset: -len * 0.4, opacity: 0 },
      ] as Keyframe[],
      { duration: 520, easing: "cubic-bezier(0.3, 0.7, 0.3, 1)", fill: "forwards" },
    );
  }

  clear(svg, 700);
}

/** Promote: rising sparkles + upward arrow on the cell. */
export function fxPromote(host: HTMLElement, cx: number, cy: number): void {
  const svg = spawnOverlay(host);
  const p = toLocal(host, cx, cy);

  // Glow halo behind the tile.
  const halo = document.createElementNS(SVG_NS, "circle");
  halo.setAttribute("cx", String(p.x));
  halo.setAttribute("cy", String(p.y));
  halo.setAttribute("r", "28");
  halo.setAttribute("fill", "#fff7d6");
  halo.setAttribute("opacity", "0.6");
  svg.appendChild(halo);
  halo.animate(
    [{ r: 28, opacity: 0.7 }, { r: 56, opacity: 0 }] as Keyframe[],
    { duration: 600, easing: "ease-out", fill: "forwards" },
  );

  // 8 sparks rising upward in a cone.
  for (let i = 0; i < 8; i++) {
    const dx = (Math.random() - 0.5) * 60;
    const dy = -(60 + Math.random() * 40);
    const spark = document.createElementNS(SVG_NS, "circle");
    spark.setAttribute("cx", String(p.x));
    spark.setAttribute("cy", String(p.y));
    spark.setAttribute("r", String(2 + Math.random() * 2));
    spark.setAttribute("fill", i % 2 ? "#ffd54a" : "#fff7ff");
    svg.appendChild(spark);
    spark.animate(
      [
        { transform: "translate(0,0)", opacity: 1 },
        { transform: `translate(${dx}px, ${dy}px)`, opacity: 0 },
      ],
      { duration: 700 + Math.random() * 150, easing: "cubic-bezier(0.2, 0.6, 0.4, 1)", fill: "forwards" },
    );
  }

  // Upward arrow head, growing then fading.
  const arrow = document.createElementNS(SVG_NS, "path");
  arrow.setAttribute("d", `M ${p.x} ${p.y - 8} L ${p.x - 10} ${p.y + 6} L ${p.x + 10} ${p.y + 6} Z`);
  arrow.setAttribute("fill", "#ffd54a");
  arrow.setAttribute("opacity", "0");
  svg.appendChild(arrow);
  arrow.animate(
    [
      { transform: "translate(0, 10px) scale(0.5)", opacity: 0 },
      { transform: "translate(0, -8px) scale(1.1)", opacity: 1, offset: 0.4 },
      { transform: "translate(0, -32px) scale(1)", opacity: 0 },
    ],
    { duration: 650, easing: "cubic-bezier(0.2, 0.6, 0.3, 1)", fill: "forwards" },
  );

  clear(svg, 850);
}

/** Reroll: a quick swirl on the queue-current swatch (called from play.ts). */
export function fxReroll(target: Element): void {
  // Two-spin scale, layered with an outline pulse. Kept light: the queue tile
  // is small and reroll fires often, so we don't want anything heavy here.
  target.animate?.(
    [
      { transform: "rotate(0deg) scale(1)", filter: "brightness(1)" },
      { transform: "rotate(180deg) scale(1.25)", filter: "brightness(1.4)", offset: 0.5 },
      { transform: "rotate(360deg) scale(1)", filter: "brightness(1)" },
    ],
    { duration: 360, easing: "cubic-bezier(0.2, 0.7, 0.3, 1)" },
  );
}
